import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { DateUtils } from '@pbt/pbt-ui-components'

import {
  type AgentChatMessage,
  type AgentChatSession,
  getSession,
  type MessageAttachment as MessageAttachmentType,
  startAgentChatSession,
} from '~/api/agentChat'
import {
  createSSEConnection,
  type SSEMessage,
  SSEMessageUtils,
} from '~/api/sseCore'
import { getTrailingIntermediateSegment } from '~/components/agent/chat/agentMessageUtils'
import type { ChatMode } from '~/components/agent/types'
import { getCurrentBusinessId, getCurrentUserId } from '~/store/reducers/auth'
import {
  ChatContext,
  InputMethod,
  trackEvent,
  TrackingEvent,
} from '~/utils/trackingProvider'

export interface UseAgentChatOptions {
  onSessionCreated?: (session: AgentChatSession) => void
  onSessionUpdate?: (session: AgentChatSession) => void
  stateDelta?: Record<string, any>
  userId?: string
}

/**
 * Minimum buffer length (chars) without trailing "..." to treat stream as final message.
 * Avoids treating short intermediate chunks as final.
 */
const STREAMING_FINAL_THRESHOLD = 15

export interface UseAgentChatReturn {
  clearMessages: () => void
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'error'
  disconnect: () => void
  error: string | null
  isResponding: boolean
  loadSession: (sessionId: string, businessId?: string) => Promise<void>
  loading: boolean
  messages: AgentChatMessage[]
  sendMessage: (params: {
    chatMode?: ChatMode
    content: string
    file?: { data: ArrayBuffer; name: string; type: string }
    inputMethod?: InputMethod
    promptMetadata?: Record<string, any>
    stateDelta?: Record<string, any>
  }) => Promise<void>
  session: AgentChatSession | null
  startSession: (stateDelta?: Record<string, any>) => Promise<void>
  streamingFinalStarted: boolean
  streamingIntermediateText: string | null
  updateMessagesFromSession: (session: AgentChatSession) => void
}

/**
 * Custom hook that manages agent chat functionality including:
 * - Session management
 * - Message sending and receiving via SSE
 * - Integration with Redux state
 * - Error handling
 */
export const useAgentChatAPI = (
  options: UseAgentChatOptions = {},
): UseAgentChatReturn => {
  const {
    userId,
    onSessionUpdate,
    onSessionCreated,
    stateDelta: defaultStateDelta,
  } = options

  const currentUserId = useSelector(getCurrentUserId)
  const currentBusinessId = useSelector(getCurrentBusinessId)

  const [messages, setMessages] = useState<AgentChatMessage[]>([])
  const [session, setSession] = useState<AgentChatSession | null>(null)
  const [loading, setLoading] = useState(false)
  const [isResponding, setIsResponding] = useState(false)
  const [connectionState, setConnectionState] = useState<
    'disconnected' | 'connecting' | 'connected' | 'error'
  >('disconnected')
  const [error, setError] = useState<string | null>(null)
  const [streamingIntermediateText, setStreamingIntermediateText] = useState<
    string | null
  >(null)
  const [streamingFinalStarted, setStreamingFinalStarted] = useState(false)

  const sessionIdRef = useRef<string | null>(null)
  const streamRef = useRef<ReadableStream<Uint8Array> | null>(null)
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const messageSentAtRef = useRef<number | null>(null)
  const hasTrackedFirstTokenRef = useRef<boolean>(false)
  const latestModelVersionRef = useRef<string | undefined>(undefined)
  const latestAgentRef = useRef<string | undefined>(undefined)
  // Track loading message ID to handle race conditions with React state batching
  const loadingMessageIdRef = useRef<string | null>(null)
  const lastMessageSessionIdRef = useRef<string | null>(null)
  // Track whether we just loaded an existing session (vs created a new one)
  const isLoadedSessionRef = useRef<boolean>(false)
  // Streaming classification: we buffer incoming tokens until we can tell intermediate vs final.
  // Trailing "…" or "..." (one-word segment) → show in loading slot; length >= threshold without that → start real message.
  const streamBufferRef = useRef<string>('')
  const isStreamingFinalRef = useRef<boolean>(false)
  const lastIntermediateTextRef = useRef<string | null>(null)

  const effectiveUserId = userId || currentUserId || 'user'

  const clearStreamingState = useCallback(() => {
    streamBufferRef.current = ''
    isStreamingFinalRef.current = false
    lastIntermediateTextRef.current = null
    setStreamingFinalStarted(false)
    setStreamingIntermediateText(null)
  }, [])

  const transformSessionEvents = useCallback(
    (events: any[]): AgentChatMessage[] =>
      events
        .filter((event: any) => {
          // Filter out timestamp-only events or events without content
          if (!event.content) return false
          if (typeof event.content === 'object' && !event.content.parts)
            return false
          return true
        })
        .map((event: any) => {
          let { content } = event
          let { sender } = event
          let hasFunctionResponse: boolean | undefined
          const attachments: MessageAttachmentType[] = []
          const invocationId = event.invocationId || event.content?.invocationId

          if (typeof content === 'object' && content?.parts) {
            const { parts } = content
            const textPart = parts.find((part: any) => part.text)
            hasFunctionResponse = parts.some((part: any) =>
              Boolean(part.functionResponse),
            )
            content = textPart?.text || ''

            const attachmentParts = parts.filter((part: any) => part.inlineData)
            attachmentParts.forEach((part: any) => {
              if (part.inlineData?.data && part.inlineData?.mimeType) {
                attachments.push({
                  data: part.inlineData.data,
                  mimeType: part.inlineData.mimeType,
                })
              }
            })
          } else if (typeof content !== 'string') {
            return undefined
          }

          // Determine sender from role if available
          if (typeof event.content === 'object' && event.content?.role) {
            sender = event.content.role === 'user' ? 'user' : 'agent'
          }

          // Preserve agent field (may come as 'agent' or 'author' in the event)
          const agent = event.agent || event.author

          return {
            ...event,
            timestamp: DateUtils.formatTime(
              moment(event.timestamp * 1000).toISOString(),
            ),
            content,
            attachments: attachments.length > 0 ? attachments : undefined,
            hasFunctionResponse,
            sender: sender || event.sender || 'agent',
            invocationId,
            agent,
          }
        })
        .filter((message: any) => message !== undefined),
    [],
  )

  const findLastUserMessageIndex = useCallback(
    (messagesList: AgentChatMessage[], beforeIndex: number): number => {
      for (let i = beforeIndex - 1; i >= 0; i--) {
        if (messagesList[i].sender === 'user') {
          return i
        }
      }
      return -1
    },
    [],
  )

  const findLastAgentMessageIndex = useCallback(
    (
      messagesList: AgentChatMessage[],
      startIndex: number,
      minIndex?: number,
    ): number => {
      const min = minIndex ?? 0
      for (let i = startIndex; i >= min; i--) {
        if (messagesList[i].sender === 'agent' && !messagesList[i].isLoading) {
          return i
        }
      }
      return -1
    },
    [],
  )

  const updateExistingMessage = useCallback(
    (
      allMessages: AgentChatMessage[],
      lastAgentMessageIndex: number,
      data: SSEMessage,
      textContent: string,
    ): AgentChatMessage[] => {
      const lastMessage = allMessages[lastAgentMessageIndex]

      const invocationId = data.invocationId || lastMessage.invocationId

      const updatedMessage: AgentChatMessage = {
        id: data.id || lastMessage.id,
        content: data.partial ? lastMessage.content + textContent : textContent,
        sender: 'agent',
        timestamp: DateUtils.formatTime(SSEMessageUtils.getTimestamp(data)),
        type: 'text',
        isLoading: false,
        isComplete: !data.partial,
        agent:
          data.agent ||
          data.author ||
          lastMessage.agent ||
          latestAgentRef.current,
        modelVersion: data.modelVersion || lastMessage.modelVersion,
        processedEvents: data.processedEvents || lastMessage.processedEvents,
        finalReportWithCitations:
          data.finalReportWithCitations || lastMessage.finalReportWithCitations,
        invocationId,
      }

      return [
        ...allMessages.slice(0, lastAgentMessageIndex),
        updatedMessage,
        ...allMessages.slice(lastAgentMessageIndex + 1),
      ]
    },
    [],
  )

  const createNewMessage = useCallback(
    (
      allMessages: AgentChatMessage[],
      loadingMessageIndex: number,
      data: SSEMessage,
      textContent: string,
    ): AgentChatMessage[] => {
      const { invocationId } = data

      const agentMessage: AgentChatMessage = {
        id: data.id || crypto.randomUUID(),
        content: textContent,
        sender: 'agent',
        timestamp: DateUtils.formatTime(SSEMessageUtils.getTimestamp(data)),
        type: 'text',
        isLoading: false,
        isComplete: !data.partial,
        agent: data.agent || data.author || latestAgentRef.current,
        modelVersion: data.modelVersion,
        processedEvents: data.processedEvents,
        finalReportWithCitations: data.finalReportWithCitations,
        invocationId,
      }

      // Find insertion point for agent message
      let insertIndex = allMessages.length

      if (loadingMessageIndex >= 0) {
        // Loading message exists: insert before it
        // Find the user message that corresponds to this loading message
        const userIndex = findLastUserMessageIndex(
          allMessages,
          loadingMessageIndex,
        )

        // Find last agent message after this user (but before loading)
        if (userIndex >= 0) {
          const agentIndex = findLastAgentMessageIndex(
            allMessages,
            loadingMessageIndex - 1,
            userIndex + 1, // minIndex: only search after user message
          )
          if (agentIndex >= 0) {
            insertIndex = agentIndex + 1
          }
        }

        // If no agent messages found for this user, insert right before loading
        if (insertIndex === allMessages.length) {
          insertIndex = loadingMessageIndex
        }
      } else {
        // No loading message: insert after last user message, or after last agent message
        const lastMessage = allMessages[allMessages.length - 1]
        if (lastMessage?.sender === 'user') {
          insertIndex = allMessages.length
        } else {
          const agentIndex = findLastAgentMessageIndex(
            allMessages,
            allMessages.length - 1,
          )
          if (agentIndex >= 0) {
            insertIndex = agentIndex + 1
          }
        }
      }

      // Insert at the calculated position
      return [
        ...allMessages.slice(0, insertIndex),
        agentMessage,
        ...allMessages.slice(insertIndex),
      ]
    },
    [],
  )

  const handleTextMessage = useCallback(
    (data: SSEMessage, textContent: string) => {
      // Already appending to current message: update it only
      if (isStreamingFinalRef.current) {
        setMessages((prev) => {
          const lastAgentMessageIndex = findLastAgentMessageIndex(
            prev,
            prev.length - 1,
          )
          if (lastAgentMessageIndex < 0) return prev
          return updateExistingMessage(
            prev,
            lastAgentMessageIndex,
            data,
            textContent,
          )
        })
        // partial: false = end of this message; next content starts a new message (new bubble)
        if (!data.partial) {
          isStreamingFinalRef.current = false
          streamBufferRef.current = ''
        }
        return
      }

      // Buffer tokens until we can classify intermediate vs final (see streamBufferRef comment above)
      streamBufferRef.current += textContent
      const buffer = streamBufferRef.current

      const trailing = getTrailingIntermediateSegment(buffer)
      if (trailing !== null) {
        streamBufferRef.current = buffer.slice(
          0,
          buffer.length - trailing.length,
        )
        lastIntermediateTextRef.current = trailing.segment
        setStreamingIntermediateText(trailing.segment)
        return
      }

      // Enough chars without "..." — create real message (master behavior for multi-word)
      if (buffer.length >= STREAMING_FINAL_THRESHOLD) {
        isStreamingFinalRef.current = true
        streamBufferRef.current = ''
        lastIntermediateTextRef.current = null
        setStreamingFinalStarted(true)
        setStreamingIntermediateText(null)
        setMessages((prev) => {
          let loadingMessageIndex = -1
          if (loadingMessageIdRef.current) {
            loadingMessageIndex = prev.findIndex(
              (msg) => msg.id === loadingMessageIdRef.current,
            )
          }
          if (loadingMessageIndex === -1) {
            loadingMessageIndex = prev.findIndex((msg) => msg.isLoading)
          }
          return createNewMessage(prev, loadingMessageIndex, data, buffer)
        })
        // partial: false = this message is complete; next chunk starts a new message
        if (!data.partial) {
          isStreamingFinalRef.current = false
        }
        return
      }

      // Still buffering: keep last one-word visible to avoid three-dot flash
      setStreamingIntermediateText(lastIntermediateTextRef.current)
    },
    [updateExistingMessage, createNewMessage],
  )

  const handleStateUpdate = useCallback(
    (data: SSEMessage) => {
      setSession((prev) => {
        if (!prev) return null

        const updatedSession = {
          ...prev,
          state: { ...prev.state, ...data.actions!.stateDelta },
        }

        onSessionUpdate?.(updatedSession)

        return updatedSession
      })
    },
    [onSessionUpdate],
  )

  const startSession = useCallback(
    async (stateDelta?: Record<string, any>) => {
      try {
        setLoading(true)
        setError(null)
        setMessages([])

        const effectiveStateDelta = stateDelta || defaultStateDelta

        const newSession = await startAgentChatSession(
          effectiveUserId,
          currentBusinessId || undefined,
          effectiveStateDelta,
        )
        sessionIdRef.current = newSession.id
        // Reset lastMessageSessionIdRef when creating a new session
        // This ensures the next message sent will be detected as NEW_CHAT
        lastMessageSessionIdRef.current = null
        // Mark as not a loaded session (it's a newly created one)
        isLoadedSessionRef.current = false
        setSession(newSession)

        const transformedMessages = transformSessionEvents(
          newSession.events || [],
        )
        setMessages(transformedMessages)

        onSessionCreated?.(newSession)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to start session:', err)
        setError('Failed to start chat session')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [
      effectiveUserId,
      currentBusinessId,
      defaultStateDelta,
      transformSessionEvents,
      onSessionCreated,
    ],
  )

  const loadSession = useCallback(
    async (sessionId: string, businessId?: string) => {
      try {
        clearStreamingState()
        setLoading(true)
        setError(null)

        setMessages([])

        const effectiveBusinessId = businessId || currentBusinessId || undefined
        const loadedSession = await getSession(
          effectiveUserId,
          sessionId,
          effectiveBusinessId,
        )
        sessionIdRef.current = loadedSession.id
        lastMessageSessionIdRef.current = null
        if (loadedSession.events && loadedSession.events.length > 0) {
          // Mark as a loaded session (has existing messages)
          isLoadedSessionRef.current = true
        } else if (loadedSession.events && loadedSession.events.length === 0) {
          // Mark as a newly created session (no messages)
          isLoadedSessionRef.current = false
        }
        setSession(loadedSession)

        const transformedMessages = transformSessionEvents(
          loadedSession.events || [],
        )
        setMessages(transformedMessages)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load session:', err)
        setError('Failed to load session')
        setLoading(false)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [
      effectiveUserId,
      currentBusinessId,
      transformSessionEvents,
      clearStreamingState,
    ],
  )

  const sendMessage = useCallback(
    async ({
      content,
      inputMethod = InputMethod.TYPING,
      promptMetadata,
      chatMode,
      file,
      stateDelta,
    }: {
      chatMode?: ChatMode
      content: string
      file?: { data: ArrayBuffer; name: string; type: string }
      inputMethod?: InputMethod
      promptMetadata?: Record<string, any>
      stateDelta?: Record<string, any>
    }) => {
      // Track if this message creates a new session
      const wasSessionCreated = !sessionIdRef.current

      // Create a new session if none exists
      if (!sessionIdRef.current) {
        await startSession(stateDelta)

        trackEvent(TrackingEvent.AI_ASSISTANT_SESSION_CREATED, {
          chat_mode: chatMode,
          is_auto_created: true,
        })
      }

      const currentSessionId = sessionIdRef.current

      let chatContext: ChatContext
      if (wasSessionCreated) {
        chatContext = ChatContext.NEW_CHAT
      } else if (
        currentSessionId &&
        lastMessageSessionIdRef.current === currentSessionId
      ) {
        chatContext = ChatContext.CURRENT_CHAT
      } else if (currentSessionId && lastMessageSessionIdRef.current === null) {
        // If lastMessageSessionIdRef is null but we have a sessionId,
        // we need to distinguish between:
        // 1. A newly created session -> NEW_CHAT
        // 2. A loaded session -> HISTORIC_CHAT
        chatContext = isLoadedSessionRef.current
          ? ChatContext.HISTORIC_CHAT
          : ChatContext.NEW_CHAT
      } else {
        chatContext = ChatContext.HISTORIC_CHAT
      }

      if (currentSessionId) {
        lastMessageSessionIdRef.current = currentSessionId
      }

      trackEvent(TrackingEvent.AI_ASSISTANT_MESSAGE_SENT, {
        chat_mode: chatMode,
        input_method: inputMethod,
        message_length: content.length,
        prompt_text: content,
        chat_context: chatContext,
        agent_session_id: currentSessionId,
        ...promptMetadata,
      })

      const removeLoadingMessage =
        (
          connectionId: string,
        ): ((prev: AgentChatMessage[]) => AgentChatMessage[]) =>
        (prev: AgentChatMessage[]) => {
          const filtered = prev.filter(
            (msg) => !(msg.isLoading && msg.id === connectionId),
          )
          if (
            filtered.length < prev.length &&
            loadingMessageIdRef.current === connectionId
          ) {
            loadingMessageIdRef.current = null
          }
          return filtered
        }

      try {
        setError(null)
        setIsResponding(true)
        setConnectionState('connecting')

        // Track message send time for first token latency
        messageSentAtRef.current = Date.now()
        hasTrackedFirstTokenRef.current = false
        latestModelVersionRef.current = undefined
        latestAgentRef.current = undefined
        clearStreamingState()

        // Convert file to base64 if present
        let fileBase64: string | undefined
        if (file) {
          const bytes = new Uint8Array(file.data)
          fileBase64 = btoa(
            Array.from(bytes, (byte) => String.fromCharCode(byte)).join(''),
          )
        }

        // Optimistically add user message to UI
        const userMessageId = crypto.randomUUID()
        const loadingMessageId = crypto.randomUUID()

        // Track loading message ID in ref to handle race conditions with React state batching
        loadingMessageIdRef.current = loadingMessageId

        const userMessage: AgentChatMessage = {
          id: userMessageId,
          content,
          sender: 'user',
          timestamp: DateUtils.formatTime(moment().toISOString()),
          type: 'text',
          attachments: file
            ? [
                {
                  data: fileBase64!,
                  mimeType: file.type,
                },
              ]
            : undefined,
        }

        // Add a loading message for the agent response
        const loadingMessage: AgentChatMessage = {
          id: loadingMessageId,
          content: '',
          sender: 'agent',
          timestamp: '',
          type: 'text',
          isLoading: true,
        }

        setMessages((prev) => [...prev, userMessage, loadingMessage])

        // Send message via SSE
        const parts: any[] = [{ text: content }]

        // Add file data if present
        if (file && fileBase64) {
          parts.push({
            inlineData: {
              data: fileBase64,
              mimeType: file.type,
            },
          })
        }

        const messagePayload = {
          appName: 'agents',
          newMessage: {
            role: 'user' as const,
            parts,
          },
          sessionId: currentSessionId,
          streaming: true,
          userId: effectiveUserId,
          ...(stateDelta && { stateDelta }),
        }

        const abortController = new AbortController()
        abortControllerRef.current = abortController

        // Capture loading message ID for this connection to handle cleanup correctly
        const connectionLoadingMessageId = loadingMessageId

        const connectionResult = await createSSEConnection(
          {
            url: '/ai-agents/run_sse',
            method: 'POST',
            data: messagePayload,
            signal: abortController.signal,
          },
          {
            onOpen: () => {
              if (sessionIdRef.current !== currentSessionId) {
                return
              }

              setConnectionState('connected')
            },
            onMessage: (data: SSEMessage) => {
              // Prevent messages from appearing in the wrong chat when switching
              if (sessionIdRef.current !== currentSessionId) {
                return
              }

              const textContent = SSEMessageUtils.extractTextContent(data)
              if (textContent.length > 0) {
                const { modelVersion, author: agentName } = data

                if (modelVersion !== undefined) {
                  latestModelVersionRef.current = modelVersion
                }
                if (agentName !== undefined) {
                  latestAgentRef.current = agentName
                }

                if (
                  !hasTrackedFirstTokenRef.current &&
                  textContent.trim().length > 0 &&
                  messageSentAtRef.current
                ) {
                  const timeToFirstToken = Date.now() - messageSentAtRef.current
                  trackEvent(TrackingEvent.AI_ASSISTANT_RESPONSE_FIRST_TOKEN, {
                    time_to_first_token_ms: timeToFirstToken,
                    agent_session_id: sessionIdRef.current,
                    model_version: modelVersion,
                    agent_name: agentName,
                  })
                  hasTrackedFirstTokenRef.current = true
                }

                handleTextMessage(data, textContent)
              }

              if (SSEMessageUtils.hasStateUpdates(data)) {
                handleStateUpdate(data)
              }
            },
            onError: (eventError: Event) => {
              // Prevent errors from affecting the wrong chat when switching
              if (sessionIdRef.current !== currentSessionId) {
                return
              }

              // eslint-disable-next-line no-console
              console.error('SSE error:', eventError)

              messageSentAtRef.current = null

              setConnectionState('error')
              setError('Connection error occurred')
              setIsResponding(false)

              // Remove loading message for this specific connection
              setMessages(removeLoadingMessage(connectionLoadingMessageId))
              clearStreamingState()
            },
            onClose: () => {
              // Prevent close events from affecting the wrong chat when switching
              if (sessionIdRef.current !== currentSessionId) {
                clearStreamingState()
                return
              }

              if (messageSentAtRef.current) {
                const totalResponseTime = Date.now() - messageSentAtRef.current
                trackEvent(TrackingEvent.AI_ASSISTANT_RESPONSE_COMPLETE, {
                  total_response_time_ms: totalResponseTime,
                  agent_session_id: sessionIdRef.current,
                  model_version: latestModelVersionRef.current,
                  agent_name: latestAgentRef.current,
                })

                // Check if the agent's response contains the word "error"
                setMessages((prevMessages) => {
                  // Find the last agent message (non-loading)
                  const lastAgentMessage = [...prevMessages]
                    .reverse()
                    .find(
                      (msg) =>
                        msg.sender === 'agent' && !msg.isLoading && msg.content,
                    )

                  if (
                    lastAgentMessage?.content &&
                    /"error"/i.test(lastAgentMessage.content)
                  ) {
                    trackEvent(
                      TrackingEvent.AI_ASSISTANT_RESPONSE_CONTAINS_ERROR,
                      {
                        agent_session_id: sessionIdRef.current,
                        message_id: lastAgentMessage.id,
                        message_length: lastAgentMessage.content.length,
                        model_version: latestModelVersionRef.current,
                        agent_name: latestAgentRef.current,
                      },
                    )
                  }

                  return removeLoadingMessage(connectionLoadingMessageId)(
                    prevMessages,
                  )
                })

                messageSentAtRef.current = null
              } else {
                // Remove loading message even if we didn't track completion
                setMessages(removeLoadingMessage(connectionLoadingMessageId))
              }

              setConnectionState('disconnected')
              setIsResponding(false)
              clearStreamingState()
            },
          },
          {
            maxRetries: 3,
            maxDuration: 30000, // 30 seconds
            baseDelay: 1000,
            maxDelay: 5000,
          },
        )

        streamRef.current = connectionResult.stream
        readerRef.current = connectionResult.reader
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to send message:', err)
        setConnectionState('error')
        setError('Failed to send message')
        setIsResponding(false)
        clearStreamingState()

        const loadingMessageIdToRemove =
          loadingMessageIdRef.current || undefined
        if (loadingMessageIdToRemove) {
          setMessages(removeLoadingMessage(loadingMessageIdToRemove))
        } else {
          // Fallback: remove all loading messages if ref is not set
          setMessages((prev) => prev.filter((msg) => !msg.isLoading))
          loadingMessageIdRef.current = null
        }
        throw err
      }
    },
    [
      effectiveUserId,
      startSession,
      handleTextMessage,
      handleStateUpdate,
      clearStreamingState,
    ],
  )

  const disconnect = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    if (readerRef.current) {
      readerRef.current.cancel().catch((readerError) => {
        // eslint-disable-next-line no-console
        console.warn('Error canceling SSE reader:', readerError)
      })
      readerRef.current = null
    }

    if (streamRef.current) {
      streamRef.current = null
    }

    clearStreamingState()
    setConnectionState('disconnected')
    setSession(null)
    sessionIdRef.current = null
    lastMessageSessionIdRef.current = null
    isLoadedSessionRef.current = false
  }, [clearStreamingState])

  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  const updateMessagesFromSession = useCallback(
    (updatedSession: AgentChatSession) => {
      const transformedMessages = transformSessionEvents(
        updatedSession.events || [],
      )
      setMessages(transformedMessages)
      setSession(updatedSession)
    },
    [transformSessionEvents],
  )

  useEffect(() => {
    // Disconnect active SSE connections when business changes
    disconnect()
    setError(null)
    setLoading(false)
    sessionIdRef.current = null
    setSession(null)
    setMessages([])
    setIsResponding(false)
    setConnectionState('disconnected')
    // Clear message tracking refs
    loadingMessageIdRef.current = null
    lastMessageSessionIdRef.current = null
    isLoadedSessionRef.current = false
  }, [currentBusinessId, disconnect])

  useEffect(
    () => () => {
      disconnect()
    },
    [disconnect],
  )

  return {
    messages,
    session,
    loading,
    isResponding,
    error,
    connectionState,
    sendMessage,
    startSession,
    loadSession,
    disconnect,
    clearMessages,
    updateMessagesFromSession,
    streamingFinalStarted,
    streamingIntermediateText,
  }
}
